/**
 * Which modified sizes are selectable for a given original-image size.
 *
 * MOCK: No per-original availability matrix exists from the backend yet; this
 * encodes the a-la-carte gating rule from the Figma notes:
 *   - Small / Extra Small original -> only the Standard (1k) modified size
 *   - Medium / Large / X Large     -> all modified sizes
 * Replace with real data from the download-authorizations endpoint once the
 * backend availability matrix exists (epic 86b8u87x4).
 *
 * Note: original `teeShirtSize` is a full word ('Small', 'Extra Small', ...),
 * never a single letter — see src/server/domain/sizes/assetSize.ts.
 */
import { ImageSize } from 'types/isomorphic';
import modifiedSizeCopy from './modifiedSizeCopy';

// Matched case- and space-insensitively: a priced original's teeShirtSize can
// arrive as 'Small', 'small', 'Extra Small', or 'ExtraSmall' depending on
// whether the backend populates teeShirtSize or falls back to definition (see
// AssetSize in src/server/domain/sizes/assetSize.ts), so normalize before comparing.
const SMALL_ORIGINAL_SIZES = new Set(['small', 'extrasmall']);
const STANDARD_RANK = 0; // modifiedSizeCopy rank for the Standard (1k) tier

const normalizeTeeShirtSize = (teeShirtSize?: string): string =>
  (teeShirtSize ?? '').toLowerCase().replace(/[^a-z]/g, '');

const availableModifiedSizes = (
  modifiedSizes: ImageSize[],
  originalSize?: { teeShirtSize?: string }
): ImageSize[] => {
  const teeShirtSize = normalizeTeeShirtSize(originalSize?.teeShirtSize);
  if (!teeShirtSize || !SMALL_ORIGINAL_SIZES.has(teeShirtSize)) {
    return modifiedSizes;
  }
  const standardOnly = modifiedSizes.filter(
    (size) => modifiedSizeCopy(size.sizeName)?.rank === STANDARD_RANK
  );
  return standardOnly.length > 0 ? standardOnly : modifiedSizes.slice(0, 1);
};

export default availableModifiedSizes;
